import { faBan, faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as fabric from 'office-ui-fabric-react';
import { IDropdownOption } from 'office-ui-fabric-react';
import React, { FC, useState } from 'react';
import { QmActionButton } from '../../../components';
import * as enums from '../../../enums';
import { locationStatus, ticketState } from '../../../enums';
import {
    blueButton,
    currentPatientPanelInformationStyle,
    expand,
    functionButtonStyles,
    greenButton,
    largeBlueFont,
    oneFieldRow,
    queueControlStyle,
    queuesFilterDropdownStyles,
    redButton,
    scrollY,
    ticketContainer,
    transparentButton
} from '../Helpers';
import { IQueue, IServiceStatistic, ITicket } from '../model';
import { useTickets } from './context/TicketsContext';
import { RelateTicketModal } from './RelateTicketModal';
import { Timer } from './Timer';
import WaitingTicketsStatisticView from './WaitingTicketsStatisticView';

interface ICompactViewProps {
    assignedTicket: ITicket | null;
    services: IServiceStatistic[];
    automatedCall: boolean | undefined;
    disableAutomated: boolean;
    presetShowCaution: boolean;
    setAutomatedCall(value: boolean | undefined, callNext?: boolean): void;
    changeLocationStatus(item: any): void;
    moveTicketToLocation(ticketId: number, state: enums.TicketState): void;
    removeTicketFromLocation(ticketId: number, state: enums.TicketState): void;
    openSendToQueueModalWindow(ticket: ITicket): void;
    setSelectedTicket(value: ITicket | null): void;
    locationStatus: number;
    setLocationStatus(value: number): void;
    allQueues: IQueue[];
    filterTickets(value: IDropdownOption): void;
    ticketsFilterTerm: string;
    setTicketsFilterTerm: Function;
}

const CompactView: FC<ICompactViewProps> = (props: ICompactViewProps) => {
    const { allTickets, waitingTickets } = useTickets();

    function disableButton(t: ITicket) {
        const disabled = false;
        if (!t) {
            return true;
        }
        if (t.state === ticketState.Invited) {
            return false;
        } else if (t.state === ticketState.Serviced) {
            return true;
        } else if (t.state === ticketState.Served) {
            return true;
        }
        return disabled;
    }

    const [showRelatePopup, setShowRelatePopup] = useState(false);
    function showRelateTicket(ticket: ITicket) {
        setShowRelatePopup(true);
    }

    const locationStatuses = [
        {
            key: locationStatus.Default,
            text: locationStatus.format(locationStatus.Default)
        },
        {
            key: locationStatus.Waiting,
            text: locationStatus.format(locationStatus.Waiting)
        },
        {
            key: locationStatus.Open,
            text: locationStatus.format(locationStatus.Open)
        },
        {
            key: locationStatus.Busy,
            text: locationStatus.format(locationStatus.Busy)
        },
        {
            key: locationStatus.Closed,
            text: locationStatus.format(locationStatus.Closed)
        },
        {
            key: locationStatus.Free,
            text: locationStatus.format(locationStatus.Free)
        },
        {
            key: locationStatus.Occupied,
            text: locationStatus.format(locationStatus.Occupied)
        }
    ];

    return (
        <>
            <fabric.Stack.Item styles={queueControlStyle}>
                <fabric.Stack styles={expand}>
                    <fabric.Stack styles={scrollY}>
                        <fabric.Stack styles={functionButtonStyles}>
                            <fabric.StackItem>
                                <fabric.Stack styles={ticketContainer} horizontal>
                                    <fabric.Text styles={largeBlueFont}>{props.assignedTicket ? `${props.assignedTicket!.number}` : '-'}</fabric.Text>
                                    <fabric.Text styles={largeBlueFont}>
                                        {props.assignedTicket && <Timer startFromTime={props.assignedTicket.modifiedAt} />}
                                    </fabric.Text>
                                </fabric.Stack>
                            </fabric.StackItem>

                            <fabric.StackItem>
                                <fabric.Stack>
                                    {props.assignedTicket && (
                                        <fabric.Stack.Item styles={currentPatientPanelInformationStyle}>
                                            <fabric.Stack>
                                                <fabric.Stack.Item styles={{ root: { marginTop: 10, textAlign: 'left' } }}>
                                                    <fabric.Text styles={largeBlueFont}>
                                                        {props.assignedTicket.patientFirstName} {props.assignedTicket.patientLastName}
                                                    </fabric.Text>
                                                </fabric.Stack.Item>
                                                <fabric.Stack.Item styles={{ root: { marginBottom: 10, textAlign: 'left' } }}>
                                                    <fabric.Text styles={largeBlueFont}>{props.assignedTicket.serviceName}</fabric.Text>
                                                </fabric.Stack.Item>
                                            </fabric.Stack>
                                        </fabric.Stack.Item>
                                    )}
                                </fabric.Stack>
                            </fabric.StackItem>

                            <fabric.StackItem>
                                <QmActionButton
                                    disabled={props.assignedTicket?.state == ticketState.Serviced}
                                    onClick={() => {
                                        if (props.assignedTicket) {
                                            props.moveTicketToLocation(props.assignedTicket.id, enums.ticketState.Invited);
                                        } else {
                                            const nextTicketId = waitingTickets[0] ? waitingTickets[0].id : 0;
                                            props.moveTicketToLocation(nextTicketId, enums.ticketState.Invited);
                                        }
                                    }}
                                    styles={blueButton}
                                    text="Kviesti"
                                    className="compactViewButtonsWidth"
                                />

                                <QmActionButton
                                    disabled={disableButton(props.assignedTicket!)}
                                    onClick={() => props.moveTicketToLocation(props.assignedTicket!.id, enums.ticketState.Serviced)}
                                    text="Atvyko"
                                    styles={greenButton}
                                    className="compactViewButtonsWidth"
                                ></QmActionButton>
                            </fabric.StackItem>

                            <fabric.StackItem>
                                <QmActionButton
                                    disabled={disableButton(props.assignedTicket!)}
                                    onClick={() => props.removeTicketFromLocation(props.assignedTicket!.id!, enums.ticketState.Removed)}
                                    text="Neatvyko"
                                    styles={redButton}
                                    className="compactViewButtonsWidth"
                                >
                                    <FontAwesomeIcon icon={faBan} color="white" />
                                </QmActionButton>

                                <QmActionButton
                                    disabled={props.assignedTicket === null || props.assignedTicket.state !== ticketState.Serviced}
                                    onClick={() => props.removeTicketFromLocation(props.assignedTicket!.id, ticketState.Served)}
                                    styles={blueButton}
                                    text="Aptarnautas"
                                    className="compactViewButtonsWidth"
                                >
                                    <FontAwesomeIcon icon={faCheck} color="white" />
                                </QmActionButton>
                            </fabric.StackItem>

                            <fabric.StackItem>
                                <fabric.DefaultButton
                                    disabled={props.assignedTicket === null || props.assignedTicket.state !== ticketState.Serviced}
                                    styles={transparentButton}
                                    onClick={() => {
                                        props.setSelectedTicket(null);
                                        props.openSendToQueueModalWindow(props.assignedTicket!);
                                    }}
                                    text="Siųsti"
                                    className="compactViewButtonsWidth"
                                ></fabric.DefaultButton>

                                <QmActionButton
                                    disabled={props.assignedTicket === null || props.assignedTicket.state !== ticketState.Serviced}
                                    onClick={() => {
                                        props.removeTicketFromLocation(props.assignedTicket!.id!, enums.ticketState.Stopped);
                                    }}
                                    text="Sustabdyti"
                                    styles={redButton}
                                    className="compactViewButtonsWidth"
                                ></QmActionButton>
                            </fabric.StackItem>

                            <fabric.StackItem>
                                <fabric.Stack>
                                    <QmActionButton
                                        onClick={() => showRelateTicket(props.assignedTicket!)}
                                        styles={transparentButton}
                                        disabled={
                                            (!props.assignedTicket && props.assignedTicket === null) ||
                                            props.assignedTicket.serviceId === 0 ||
                                            props.assignedTicket.state !== ticketState.Serviced
                                        }
                                        text="Identifikuoti"
                                    ></QmActionButton>
                                </fabric.Stack>
                            </fabric.StackItem>
                            <fabric.StackItem>
                                <fabric.SearchBox
                                    className="ticketsSearchBox"
                                    placeholder="Paieška"
                                    onClear={() => props.setTicketsFilterTerm('')}
                                    onChange={(_, newValue) => props.setTicketsFilterTerm(newValue)}
                                />
                            </fabric.StackItem>

                            <fabric.Stack.Item>
                                <fabric.Stack style={{ marginTop: '3vw' }} styles={oneFieldRow} horizontal>
                                    <fabric.Text variant={'large'}>Pranešimo signalas</fabric.Text>
                                    <fabric.Toggle
                                        styles={{ root: { marginBottom: '0px' } }}
                                        checked={props.presetShowCaution}
                                        disabled={props.automatedCall}
                                        onChange={() => {
                                            props.setAutomatedCall(undefined, !props.presetShowCaution);
                                            localStorage.setItem('presetCaution', (!props.presetShowCaution).toString());
                                        }}
                                    ></fabric.Toggle>
                                </fabric.Stack>
                            </fabric.Stack.Item>
                            <fabric.Stack.Item>
                                <fabric.Stack styles={oneFieldRow} horizontal>
                                    <fabric.Text variant={'large'}>Automatinis kvietimas</fabric.Text>
                                    <fabric.Toggle
                                        styles={{ root: { marginBottom: '0px' } }}
                                        checked={props.automatedCall}
                                        disabled={props.assignedTicket != null || props.disableAutomated}
                                        onChange={() => {
                                            props.setAutomatedCall(!props.automatedCall, !props.automatedCall ? true : props.presetShowCaution);
                                            localStorage.setItem('automatedCall', (!props.automatedCall).toString());
                                            localStorage.setItem('presetCaution', (!props.automatedCall ? true : props.presetShowCaution).toString());
                                        }}
                                    />
                                </fabric.Stack>
                            </fabric.Stack.Item>

                            <fabric.Stack.Item>
                                <fabric.Stack styles={oneFieldRow} horizontal>
                                    <fabric.Text variant={'large'}>Ekrano būsena</fabric.Text>
                                    <fabric.Dropdown
                                        placeholder="Pažymėti būseną"
                                        onChange={(e, item) => {
                                            props.setLocationStatus(item!.key as number);
                                            props.changeLocationStatus(item!.key);
                                        }}
                                        options={locationStatuses as fabric.IDropdownOption[]}
                                        selectedKey={props.locationStatus}
                                        className={'dropdownColor'}
                                    />
                                </fabric.Stack>
                            </fabric.Stack.Item>

                            {props.allQueues.length && (
                                <fabric.Stack.Item styles={queuesFilterDropdownStyles} align="center">
                                    <fabric.Dropdown
                                        label="Pasirinkite eiles"
                                        multiSelect={true}
                                        options={props.allQueues.map(x => {
                                            return { selected: x.isSelectedInLocation, key: x.id, text: x.name ?? 'Eilė neturi pavadinimo' };
                                        })}
                                        onChange={({}, option?: fabric.IDropdownOption | undefined) => option && props.filterTickets(option)}
                                    />
                                </fabric.Stack.Item>
                            )}

                            <WaitingTicketsStatisticView services={props.services} />
                        </fabric.Stack>
                    </fabric.Stack>
                </fabric.Stack>
            </fabric.Stack.Item>

            {showRelatePopup && <RelateTicketModal showMe={showRelatePopup} setShowRelate={setShowRelatePopup} ticket={props.assignedTicket} />}
        </>
    );
};

export default CompactView;
